<script>
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import UserDropdown from '@/components/layouts/dashboard/UserDropdown';
import animate from '@/modules/animate-css';

export default {
  name: 'Sidebar',
  components: {
    SidebarLink,
    SidebarLinkGroup,
    UserDropdown
  },
  data() {
    return {
      openOnMobile: false
    };
  },
  computed: {
    customSupportUrl() {
      const user = this.$store.state.user;
      const metadata = this.$store.state.metadata;

      if (!user.isAdmin && user.account.plan === 'agency' && metadata.supportUrl) {
        return metadata.supportUrl;
      } else {
        return false;
      }
    }
  },
  methods: {
    // Toggle the sidebar's visibility on mobile devices
    toggleMobileSidebar() {
      this.openOnMobile = !this.openOnMobile;

      // Animate the mobile menu
      this.$nextTick(() => {
        const sidebarBody = this.$refs.sidebarBody;
        const animation = this.openOnMobile ? 'fadeIn' : 'fadeOut';
        sidebarBody.classList.add('is-animating');
        animate(sidebarBody, animation, 0.25, () => sidebarBody.classList.remove('is-animating'));
      });
    }
  }
};
</script>

<template>
  <nav class="sidebar">
    <!-- Sidebar header -->
    <div class="sidebar-header">
      <!-- Logo -->
      <div class="logo">
        <router-link :to="{ name: 'Home' }">
          <img :src="$store.state.metadata.logoDark" :alt="$t('Logo')" />
        </router-link>
      </div>

      <!-- User menu -->
      <div class="user-dropdown">
        <user-dropdown />
      </div>

      <!-- Mobile toggle -->
      <div class="mobile-toggle">
        <el-button type="text" @click="toggleMobileSidebar">
          <i
            :class="{
              'el-icon-fa-fw': true,
              'el-icon-fa-times': openOnMobile,
              'el-icon-fa-bars': !openOnMobile
            }"
          />
        </el-button>
      </div>
    </div>

    <!-- Sidebar body -->
    <div
      ref="sidebarBody"
      :class="{
        'sidebar-body': true,
        'sidebar-open': openOnMobile
      }"
    >
      <sidebar-link-group :title="$t('Main')">
        <sidebar-link :text="$t('Sites')" :to="{ name: 'Sites' }" icon="el-icon-fa-browser" />
        <sidebar-link
          v-if="$store.state.user.isAdmin"
          :text="$t('Users')"
          :to="{ name: 'Users' }"
          icon="el-icon-fa-user"
        />
        <sidebar-link
          :text="$t('Support')"
          :href="customSupportUrl || undefined"
          :to="customSupportUrl ? undefined : { name: 'Support' }"
          icon="el-icon-fa-life-ring"
        />
        <sidebar-link
          v-if="$store.state.user.canManageBilling"
          :text="$t('Billing')"
          :to="{ name: 'Billing' }"
          icon="el-icon-fa-credit-card-front"
        />
        <sidebar-link
          v-if="$store.state.user.canManageSettings"
          :text="$t('Settings')"
          :to="{ name: 'Settings' }"
          icon="el-icon-fa-cog"
        />
      </sidebar-link-group>

      <!-- Custom sidebar slot -->
      <slot />

      <!-- Upgrade banner -->
      <router-link
        v-if="$store.state.user.account.plan === 'personal' && $store.state.user.canManageBilling"
        :to="{ name: 'Billing' }"
        class="upgrade-banner"
      >
        <i class="el-icon-fa-unlock" />
        {{ $t('Upgrade to unlock priority support, commercial sites, and more features!') }}
      </router-link>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.sidebar {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  width: $--dashboard-sidebar-width;
  display: flex;
  flex-direction: column;
  font-size: $--font-size-small;
  background-color: $--color-gray-90;
  color: $--color-gray-20;
  user-select: none;

  @include breakpoint('sm-and-down') {
    position: static;
    width: 100%;
  }
}

.sidebar-header {
  display: flex;
  flex: 0 0 auto;
  height: 4.25rem;
  align-items: center;
  padding: 0 0.5rem;
  margin-bottom: 0.25rem;

  @include breakpoint('sm-and-down') {
    height: $--dashboard-sidebar-mobile-height;
    padding: 0 0.25rem;
    margin-bottom: 0;
  }

  .logo {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    height: 2.5rem;
    padding: 0.1rem 0.5rem;

    img {
      display: block;
      max-width: 100%;
      max-height: 2rem;
    }

    @include breakpoint('sm-and-down') {
      justify-content: center;
      order: 2;

      img {
        max-width: 10rem;
      }
    }
  }

  .user-dropdown {
    flex: 0 0 auto;
    width: auto;
    padding: 0 0.25rem;

    @include breakpoint('sm-and-down') {
      order: 3;

      /deep/ .el-button {
        padding-top: 0.4rem;
        padding-bottom: 0.4rem;
      }
    }
  }

  .mobile-toggle {
    display: none;

    @include breakpoint('sm-and-down') {
      display: inline-block;
      order: 1;
      flex: 0 0 auto;
      text-align: right;

      i {
        font-size: 1.25rem;
        color: $--color-white;
        margin: 0 0.25rem;
      }
    }
  }
}

.sidebar-body {
  flex: 1 1 auto;
  overflow: auto;

  @include breakpoint('sm-and-down') {
    position: absolute;
    top: $--dashboard-sidebar-mobile-height;
    left: 0;
    right: 0;
    z-index: $--index-top + 10;
    background-color: $--color-gray-90;
    padding-top: 1rem;

    &:not(.sidebar-open):not(.is-animating) {
      display: none;
    }
  }
}

.upgrade-banner {
  position: relative;
  display: block;
  margin: 0 1.25rem 1.5rem 1.25rem;
  background-color: $--color-success;
  font-size: 0.75rem;
  color: $--color-white;
  line-height: 1.4;
  padding: 1rem 1rem 1rem 2.75rem;

  i {
    position: absolute;
    top: 1.25rem;
    left: 1rem;
    font-size: 1.2rem;
    transition: 0.2s transform ease-in-out;
  }

  &:hover i {
    transform: rotate(-25deg);
  }

  // Stitch border
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    right: 2px;
    bottom: 2px;
    left: 2px;
    border: dashed 1px rgba($--color-white, 0.2);
  }
}
</style>
